import { ImportCustomerRetailPricesMutation } from "~/gql/mutations/customerRetailPrices.generated";
import {
	ActivateCustomerMutation,
	AddBillingAddressMutation,
	AddContactMutation,
	AddCustomerLocationSettingsNoteMutation,
	AddCustomerMutation,
	AddCustomerUpgradeMutation,
	ClearCustomerLocationNoteMutation,
	ClearCustomerNoteMutation,
	DeactivateCustomerUpgradeMutation,
	DeleteCustomerMutation,
	EditCustomerLocationSettingsNoteMutation,
	EditCustomerMutation,
	InactivateCustomerMutation,
	MergeCustomerMutation,
	RemoveBillingAddressMutation,
	RemoveContactMutation,
	ReplaceCustomerSalesPeopleMutation,
	ReplaceLocationSalesPeopleMutation,
	SetCustomerDepositRequirementMutation,
	SetCustomerDisabledVolumePoolsMutation,
	SetCustomerLocationCoordinatesMutation,
	SetCustomerLocationDeliveryScheduleMutation,
	SetCustomerLocationNoteMutation,
	SetCustomerLocationReceivingEmailMutation,
	SetCustomerLocationServicingSiteMutation,
	SetCustomerMinimumFreightChargeMutation,
	SetCustomerNoteMutation,
	SetCustomerOrderEntryUnitMutation,
	SetCustomerPriceEntryUnitMutation,
	SetCustomerTaxDetailsMutation,
	SetCustomerTypeMutation,
	ToggleCustomerUpgradeDefaultMutation,
	UpdateBillingAddressMutation,
	UpdateContactMutation,
	UpdateCustomerLocationSendInvoiceEmailsMutation,
	UpdateCustomerLocationSendOrderEmailsMutation,
	UpdateCustomerSendInvoiceEmailsMutation,
	UpdateCustomerSendOrderEmailsMutation,
	UpdateCustomerUpgradeMutation
} from "~/gql/mutations/customers.generated";
import {
	RecordContactTextConsentMutation,
	StopContactTextsMutation
} from "~/gql/mutations/customerTextConsent.generated";
import {
	GetCustomerInvoiceRecipientsQuery,
	GetCustomerNamesForUserAdminQuery,
	GetCustomerQuery,
	GetCustomersByNamesQuery,
	GetCustomersQuery,
	GetCustomersTermsByIdsQuery,
	GetEffectiveDeliveryScheduleQuery
} from "~/gql/queries/customer.generated";
import { GetCustomerOrderStatsQuery, GetCustomerTopSkusQuery } from "~/gql/queries/customerHealth.generated";
import { GetCustomerRetailPricesQuery } from "~/gql/queries/customerRetailPrices.generated";
import { GetCustomerTextConsentsQuery } from "~/gql/queries/customerTextConsents.generated";

export const customerEndpoints = {
	ActivateCustomer: {
		invalidatesTags: ["Customer"] as AddTagTypes,
		transformResponse: (res: ActivateCustomerMutation) => res
	},
	AddBillingAddress: {
		invalidatesTags: ["Address", "Customer"] as AddTagTypes,
		transformResponse: (res: AddBillingAddressMutation) => res
	},
	AddContact: {
		invalidatesTags: ["Contact", "Customer"] as AddTagTypes,
		transformResponse: (res: AddContactMutation) => res
	},
	AddCustomer: {
		invalidatesTags: ["Customer", "Learning"] as AddTagTypes,
		transformResponse: (res: AddCustomerMutation) => res
	},
	AddCustomerLocationSettingsNote: {
		invalidatesTags: ["Customer", "Location"] as AddTagTypes,
		transformResponse: (res: AddCustomerLocationSettingsNoteMutation) => res
	},
	AddCustomerUpgrade: {
		invalidatesTags: ["Customer"] as AddTagTypes,
		transformResponse: (res: AddCustomerUpgradeMutation) => res
	},
	ClearCustomerLocationNote: {
		invalidatesTags: ["Customer", "Location"] as AddTagTypes,
		transformResponse: (res: ClearCustomerLocationNoteMutation) => res
	},
	ClearCustomerNote: {
		invalidatesTags: ["Customer"] as AddTagTypes,
		transformResponse: (res: ClearCustomerNoteMutation) => res
	},
	DeactivateCustomerUpgrade: {
		invalidatesTags: ["Customer"] as AddTagTypes,
		transformResponse: (res: DeactivateCustomerUpgradeMutation) => res
	},
	DeleteCustomer: {
		invalidatesTags: ["Customer"] as AddTagTypes,
		transformResponse: (res: DeleteCustomerMutation) => res
	},
	EditCustomer: {
		invalidatesTags: ["Customer"] as AddTagTypes,
		transformResponse: (res: EditCustomerMutation) => res
	},
	EditCustomerLocationSettingsNote: {
		invalidatesTags: ["Customer", "Location"] as AddTagTypes,
		transformResponse: (res: EditCustomerLocationSettingsNoteMutation) => res
	},
	GetCustomer: {
		providesTags: ["Customer"] as AddTagTypes,
		transformResponse: (res: GetCustomerQuery) => res
	},
	GetCustomerInvoiceRecipients: {
		providesTags: ["Contact", "Customer"] as AddTagTypes,
		transformResponse: (res: GetCustomerInvoiceRecipientsQuery) => res
	},
	GetCustomerMinimumFreight: { providesTags: ["Customer"] as AddTagTypes },
	GetCustomerNamesForUserAdmin: {
		providesTags: ["Customer"] as AddTagTypes,
		transformResponse: (res: GetCustomerNamesForUserAdminQuery) => res
	},
	GetCustomerOrderStats: {
		providesTags: ["Customer", "Orders"] as AddTagTypes,
		transformResponse: (res: GetCustomerOrderStatsQuery) => res
	},
	GetCustomerRetailPrices: {
		providesTags: ["CustomerRetailPrices"] as AddTagTypes,
		transformResponse: (res: GetCustomerRetailPricesQuery) => res
	},
	GetCustomers: {
		providesTags: ["Customer"] as AddTagTypes,
		transformResponse: (res: GetCustomersQuery) => res
	},
	GetCustomersByNames: {
		providesTags: ["Customer"] as AddTagTypes,
		transformResponse: (res: GetCustomersByNamesQuery) => res
	},
	GetCustomersTermsByIds: {
		providesTags: ["Customer"] as AddTagTypes,
		transformResponse: (res: GetCustomersTermsByIdsQuery) => res
	},
	GetCustomerTextConsents: {
		providesTags: ["TextConsents"] as AddTagTypes,
		transformResponse: (res: GetCustomerTextConsentsQuery) => res
	},
	GetCustomerTopSkus: {
		providesTags: ["Customer", "Orders"] as AddTagTypes,
		transformResponse: (res: GetCustomerTopSkusQuery) => res
	},
	GetEffectiveDeliverySchedule: {
		providesTags: ["Location", "RouteDeliveryScheduleConfig"] as AddTagTypes,
		transformResponse: (res: GetEffectiveDeliveryScheduleQuery) => res
	},
	ImportCustomerRetailPrices: {
		invalidatesTags: ["CustomerRetailPrices"] as AddTagTypes,
		transformResponse: (res: ImportCustomerRetailPricesMutation) => res
	},
	InactivateCustomer: {
		invalidatesTags: ["Customer"] as AddTagTypes,
		transformResponse: (res: InactivateCustomerMutation) => res
	},
	MergeCustomer: {
		invalidatesTags: ["Customer", "Location"] as AddTagTypes,
		transformResponse: (res: MergeCustomerMutation) => res
	},
	RecordContactTextConsent: {
		invalidatesTags: ["TextConsents"] as AddTagTypes,
		transformResponse: (res: RecordContactTextConsentMutation) => res
	},
	RemoveBillingAddress: {
		invalidatesTags: ["Address", "Customer"] as AddTagTypes,
		transformResponse: (res: RemoveBillingAddressMutation) => res
	},
	RemoveContact: {
		invalidatesTags: ["Contact", "Customer", "TextConsents"] as AddTagTypes,
		transformResponse: (res: RemoveContactMutation) => res
	},
	ReplaceCustomerSalesPeople: {
		invalidatesTags: ["Customer", "Location", "Orders"] as AddTagTypes,
		transformResponse: (res: ReplaceCustomerSalesPeopleMutation) => res
	},
	ReplaceLocationSalesPeople: {
		invalidatesTags: ["Customer", "Location", "Orders"] as AddTagTypes,
		transformResponse: (res: ReplaceLocationSalesPeopleMutation) => res
	},
	SetCustomerDepositRequirement: {
		invalidatesTags: ["Customer", "Orders"] as AddTagTypes,
		transformResponse: (res: SetCustomerDepositRequirementMutation) => res
	},
	SetCustomerLocationDeliverySchedule: {
		invalidatesTags: ["Customer", "Location"] as AddTagTypes,
		transformResponse: (res: SetCustomerLocationDeliveryScheduleMutation) => res
	},
	SetCustomerDisabledVolumePools: {
		invalidatesTags: ["Customer"] as AddTagTypes,
		transformResponse: (res: SetCustomerDisabledVolumePoolsMutation) => res
	},
	SetCustomerLocationReceivingEmail: {
		invalidatesTags: ["Customer", "Location"] as AddTagTypes,
		transformResponse: (res: SetCustomerLocationReceivingEmailMutation) => res
	},
	SetCustomerLocationCoordinates: {
		invalidatesTags: ["Customer", "Location"] as AddTagTypes,
		transformResponse: (res: SetCustomerLocationCoordinatesMutation) => res
	},
	SetCustomerLocationNote: {
		invalidatesTags: ["Customer", "Location"] as AddTagTypes,
		transformResponse: (res: SetCustomerLocationNoteMutation) => res
	},
	SetCustomerLocationServicingSite: {
		invalidatesTags: ["Customer", "Location"] as AddTagTypes,
		transformResponse: (res: SetCustomerLocationServicingSiteMutation) => res
	},
	SetCustomerMinimumFreightCharge: {
		invalidatesTags: ["Customer"] as AddTagTypes,
		transformResponse: (res: SetCustomerMinimumFreightChargeMutation) => res
	},
	SetCustomerNote: {
		invalidatesTags: ["Customer"] as AddTagTypes,
		transformResponse: (res: SetCustomerNoteMutation) => res
	},
	SetCustomerOrderEntryUnit: {
		invalidatesTags: ["Customer"] as AddTagTypes,
		transformResponse: (res: SetCustomerOrderEntryUnitMutation) => res
	},
	SetCustomerPriceEntryUnit: {
		invalidatesTags: ["Customer"] as AddTagTypes,
		transformResponse: (res: SetCustomerPriceEntryUnitMutation) => res
	},
	SetCustomerStatementDelivery: {
		invalidatesTags: ["Customer"] as AddTagTypes
	},
	SetCustomerTaxDetails: {
		invalidatesTags: ["Customer", "Orders"] as AddTagTypes,
		transformResponse: (res: SetCustomerTaxDetailsMutation) => res
	},
	SetCustomerType: {
		invalidatesTags: ["Customer"] as AddTagTypes,
		transformResponse: (res: SetCustomerTypeMutation) => res
	},
	StopContactTexts: {
		invalidatesTags: ["NotificationSettings", "TextConsents"] as AddTagTypes,
		transformResponse: (res: StopContactTextsMutation) => res
	},
	ToggleCustomerUpgradeDefault: {
		invalidatesTags: ["Customer"] as AddTagTypes,
		transformResponse: (res: ToggleCustomerUpgradeDefaultMutation) => res
	},
	UpdateBillingAddress: {
		invalidatesTags: ["Address", "Customer"] as AddTagTypes,
		transformResponse: (res: UpdateBillingAddressMutation) => res
	},
	UpdateContact: {
		invalidatesTags: ["Contact", "Customer", "TextConsents"] as AddTagTypes,
		transformResponse: (res: UpdateContactMutation) => res
	},
	UpdateCustomerLocationSendInvoiceEmails: {
		invalidatesTags: ["Customer", "Location", "Orders"] as AddTagTypes,
		transformResponse: (res: UpdateCustomerLocationSendInvoiceEmailsMutation) => res
	},
	UpdateCustomerLocationSendOrderEmails: {
		invalidatesTags: ["Customer", "Location", "Orders"] as AddTagTypes,
		transformResponse: (res: UpdateCustomerLocationSendOrderEmailsMutation) => res
	},
	UpdateCustomerSendInvoiceEmails: {
		invalidatesTags: ["Customer", "Orders"] as AddTagTypes,
		transformResponse: (res: UpdateCustomerSendInvoiceEmailsMutation) => res
	},
	UpdateCustomerSendOrderEmails: {
		invalidatesTags: ["Customer", "Orders"] as AddTagTypes,
		transformResponse: (res: UpdateCustomerSendOrderEmailsMutation) => res
	},
	UpdateCustomerUpgrade: {
		invalidatesTags: ["Customer"] as AddTagTypes,
		transformResponse: (res: UpdateCustomerUpgradeMutation) => res
	}
};
